import { EntityEditorControllerApiFactory } from "@eu.mdotm.api/sphere";
import { useApiGen } from "$root/api/hooks";
import type { MinimumDialogProps } from "$root/components/spawnable/type";
import { useDebouncedNameUniquenessChecker } from "$root/functional-areas/named-entities/uniqueness";
import type { ReviewInstrument } from "$root/functional-areas/proxies/const";
import { axiosExtract } from "$root/third-party-integrations/axios";
import { FormController } from "$root/third-party-integrations/react-hook-form";
import { FormFields } from "$root/ui-lib/form/FormFields";
import { qualifier } from "$root/utils";
import { unreachable } from "@mdotm/utils/runtime";
import { zodResolver } from "@hookform/resolvers/zod";
import {
	type IconName,
	ActionText,
	CircularProgressBar,
	Dialog,
	DialogFooter,
	Icon,
	Row,
	Text,
} from "@mdotm/shadcn/legacy/components";
import { Radio, RadioGroup, Button, FormField, SubmitButton } from "@mdotm/shadcn/mdotm/components";
import type { MaybePromise } from "@mdotm/utils/promise";
import { adaptAnimatedNodeProvider, spawn } from "@mdotm/shadcn/legacy/react-extensions";
import { themeCSSVars } from "@mdotm/shadcn/legacy/themes";
import { noop } from "@mdotm/shadcn/legacy/utils";
import type { CustomCellRendererProps } from "ag-grid-react";
import { useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import * as z from "zod";
import { type RowBlock, rowBlock } from "./blocking-issue";

export function instrumentIssueColdDef(): {
	cellRenderer(params: CustomCellRendererProps<ReviewInstrument>): React.JSX.Element | string;
} {
	function cellRenderer(params: CustomCellRendererProps<ReviewInstrument>) {
		function Cell(params: CustomCellRendererProps<ReviewInstrument>): React.JSX.Element | string {
			const { t } = useTranslation();
			const row = params.data;
			if (!row) {
				return "";
			}

			const block = rowBlock(row);
			if (block?.kind === "missingData") {
				return <BlockedIssue block={block} />;
			}

			if (!row.issue) {
				return "";
			}

			if (row?.hasBeenReviewed) {
				return (
					<Row className="h-full w-full" alignItems="center">
						<Issue title="Instrument updated" state="completed" icon="Icon-Ptf-status-SMALL" onClickAsync={noop} />
					</Row>
				);
			}

			switch (row.issue) {
				case "DELISTED_INSTRUMENT":
				case "NON_SUPPORTED_CURRENCY":
				case "MATURED_BOND":
					return <BlockedIssue block={{ kind: "issue", issue: row.issue }} />;
				case "MISSING_RISK_MODEL":
				case "RISK_MODEL_SUGGESTION_REQUIRES_REVIEW":
				case "NOT_SUPPORTED_BOND":
				case "MISSING_CLOSES":
					return (
						<Row className="h-full w-full" alignItems="center">
							<Issue
								title={t(`INSTRUMENT.ISSUE.${row.issue}`)}
								icon="Suggestion"
								state="suggestion"
								onClickAsync={noop}
							/>
						</Row>
					);
				case "UNRECOGNIZED_INSTRUMENT":
					return (
						<Row className="h-full w-full" alignItems="center">
							<Issue title={t(`INSTRUMENT.ISSUE.${row.issue}`)} icon="Alert" state="required" onClickAsync={noop} />
						</Row>
					);
				default:
					unreachable(row.issue);
			}
		}
		return Cell(params);
	}

	return { cellRenderer };
}

/** A blocked row's issue: read-only, nothing to click. */
type BlockedIssueProps = { block: RowBlock };

export function BlockedIssue({ block }: BlockedIssueProps): React.JSX.Element {
	const { t } = useTranslation();
	const title =
		block.kind === "issue" ? t(`INSTRUMENT.ISSUE.${block.issue}`) : t("INSTRUMENT.ISSUE.MISSING_INSTRUMENT_DATA");
	return (
		<Row className="h-full w-full" alignItems="center">
			<Issue title={title} icon="Locked" state="blocked" />
		</Row>
	);
}

const colorIssueByState = {
	required: themeCSSVars.palette_W500,
	completed: themeCSSVars.palette_P500,
	blocked: themeCSSVars.palette_N500,
	suggestion: themeCSSVars.palette_S500,
} as const;

type IssueState = keyof typeof colorIssueByState;

function Issue(props: { title: string; state: IssueState; icon: IconName; onClickAsync?(): MaybePromise<void> }) {
	if (props.onClickAsync) {
		return (
			<ActionText
				className="flex items-center gap-1 overflow-x-hidden"
				title={props.title}
				onClickAsync={props.onClickAsync}
				as="div"
			>
				<Icon size={18} icon={props.icon} color={colorIssueByState[props.state]} />
				<span className="truncate">{props.title}</span>
			</ActionText>
		);
	}

	return (
		<div className="flex items-center gap-1 overflow-x-hidden">
			<Icon size={18} icon={props.icon} color={colorIssueByState[props.state]} />
			<Text className="truncate" type="Body/M/Medium">
				{props.title}
			</Text>
		</div>
	);
}

type UnrecognizedInstrumentDialogProps = MinimumDialogProps & SpawnUnrecognizedInstrumentDialogProps;
type UnrecognizedInstrumentForm =
	| {
			strategy: "NEW_IDENTIFIER";
			identifier: string;
	  }
	| {
			strategy: "PROVIDE_RISK_MODEL";
	  };

function UnrecognizedInstrumentDialog(props: UnrecognizedInstrumentDialogProps) {
	const { t } = useTranslation();

	const editorApi = useApiGen(EntityEditorControllerApiFactory);
	const { checkingNameUniqueness: checkingTickerValidity, checkIfNameIsAvailable: checkTickerValidity } =
		useDebouncedNameUniquenessChecker({
			isNameAvailableApi: (identifier) => axiosExtract(editorApi.validateTicker(identifier)),
			currentName: "",
		});

	const form = useForm<UnrecognizedInstrumentForm>({
		defaultValues: undefined,
		resolver: zodResolver(
			z.discriminatedUnion("strategy", [
				z.object({ strategy: z.literal("PROVIDE_RISK_MODEL") }),
				z.object({
					strategy: z.literal("NEW_IDENTIFIER"),
					identifier: z
						.string()
						.refine((identifier) => checkTickerValidity(identifier), { message: "Identifier not valid" }),
				}),
			]),
		),
	});

	return (
		<Dialog
			size="medium"
			show={props.show}
			onAnimationStateChange={props.onAnimationStateChange}
			header="Unrecognized instrument"
			onSubmitAsync={form.handleSubmit(props.onSubmit)}
			footer={
				<DialogFooter
					primaryAction={
						<SubmitButton
							palette="primary"
							data-qualifier={qualifier.component.button("Dialog/Submit")}
							disabled={form.watch("strategy") == null}
						>
							{form.watch("strategy") === "PROVIDE_RISK_MODEL" ? t("BUTTON.CONTINUE") : t("BUTTON.DONE")}
						</SubmitButton>
					}
					neutralAction={
						<Button
							palette="tertiary"
							onClick={props.onClose}
							data-qualifier={qualifier.component.button("Dialog/Cancel")}
						>
							{t("BUTTON.CANCEL")}
						</Button>
					}
				/>
			}
		>
			<Text as="p" type="Body/M/Medium" className="mb-6">
				Choose how to proceed to identify this instrument
			</Text>
			<FormController
				control={form.control}
				name="strategy"
				render={({ field: controllerProps }) => (
					<RadioGroup {...controllerProps}>
						<div className="flex flex-col gap-4 mb-4">
							<Radio
								data-qualifier="UnrecognizedInstrumentDialog/strategy/PROVIDE_RISK_MODEL"
								value="PROVIDE_RISK_MODEL"
							>
								Provide instrument classification
							</Radio>
							<Radio data-qualifier="UnrecognizedInstrumentDialog/strategy/NEW_IDENTIFIER" value="NEW_IDENTIFIER">
								Provide a valid identifier
							</Radio>
						</div>
					</RadioGroup>
				)}
			/>

			{form.watch("strategy") === "NEW_IDENTIFIER" && (
				<div className="flex flex-col min-w-0 gap-4">
					<div>
						<FormField label="Unrecognized instrument">{props.instrument || "-"}</FormField>
					</div>
					<FormFields.Text
						control={form.control}
						formState={form.formState}
						name="identifier"
						label="ISIN, CUSIP or BLOOMBERG TICKER"
						placeholder="Insert a valid instrument identifier"
						data-qualifier="UnrecognizedInstrumentDialog/identifier"
						rightContent={
							checkingTickerValidity ? <CircularProgressBar className="w-3" value="indeterminate" /> : undefined
						}
					/>
				</div>
			)}
		</Dialog>
	);
}

type SpawnUnrecognizedInstrumentDialogProps = {
	instrument: string;
	onSubmit(params: UnrecognizedInstrumentForm): void;
};

export function spawnUnrecognizedInstrumentDialog(props: SpawnUnrecognizedInstrumentDialogProps) {
	return spawn<void>(
		adaptAnimatedNodeProvider(({ resolve, show, onHidden }) => (
			<UnrecognizedInstrumentDialog
				show={show}
				onClose={() => resolve()}
				onAnimationStateChange={(state) => state === "hidden" && onHidden()}
				instrument={props.instrument}
				onSubmit={(fields) => {
					props.onSubmit(fields);
					resolve();
				}}
			/>
		)),
	);
}
